import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  FileCheck,
  FileText,
  Upload,
  Download,
  Eye,
  Trash2,
  Edit,
  Save,
  X,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Bookmark,
  Star,
  Share,
  Info,
  ExternalLink,
  Archive,
  Send,
  Flag,
  Heart,
  ThumbsUp,
  ThumbsDown,
  FileUp,
  FileDown,
  FileX,
  Volume2,
  VolumeX,
  Headphones,
  Speaker,
  Radio,
  Music,
  Video,
  Camera,
  Image,
  File,
  Folder,
  FolderOpen,
  HardDrive,
  Cloud,
  Wifi,
  WifiOff,
  Signal,
  Battery,
  BatteryLow,
  Power,
  PowerOff,
  Activity,
  Zap,
  Shield,
  Lock,
  Key,
  Users,
  Building,
  Calendar,
  Phone,
  Mail,
  MapPin,
  CheckSquare,
  Square as SquareIcon,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeft,
  Home,
  Wrench,
  FileBarChart,
  Cog,
  Settings,
  Link,
  Monitor,
  Tablet,
  Smartphone,
  Lightbulb,
  Cpu,
  Network,
  Database,
  Layers,
  User,
  Languages,
  Mic,
  Play,
  Pause,
  Square,
  RefreshCw,
  Copy,
  Download as DownloadIcon,
  Globe,
  Users2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Target,
  Brain,
  MessageSquare,
  AlertCircle,
  CheckSquare as CheckSquareIcon,
  DollarSign,
  CreditCard,
  Calculator,
  TrendingUp,
  TrendingDown,
  PieChart,
  BarChart3,
  Receipt,
  Bot,
  Scan,
  Zap as ZapIcon,
  Shield as ShieldIcon,
  HelpCircle,
  Package,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast } from '../common/Toast';
import { invoiceValidationService } from '../../services/invoiceValidationService';

// Custom Dropdown Component for Invoice Validation Actions
const InvoiceValidationActionDropdown = ({
  invoice,
  onView,
  onEdit,
  onProcess,
  onDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 250;

      setOpenUpward(spaceBelow < menuHeight);
    }
    setIsOpen(!isOpen);
  };

  const handleView = () => {
    setIsOpen(false);
    if (invoice && (invoice.id || invoice._id)) {
      onView(invoice);
    }
  };

  const handleEdit = () => {
    setIsOpen(false);
    if (invoice && (invoice.id || invoice._id)) {
      onEdit(invoice);
    }
  };

  const handleProcess = () => {
    setIsOpen(false);
    if (invoice && (invoice.id || invoice._id)) {
      onProcess(invoice);
    }
  };

  // const handleDelete = () => {
  //   setIsOpen(false);
  //   if (invoice && (invoice.id || invoice._id)) {
  //     onDelete(invoice);
  //   }
  // };

  return (
    <div
      className="dropdown"
      ref={dropdownRef}
      style={{ position: 'relative', zIndex: 1050 }}
    >
      <button
        ref={buttonRef}
        className="btn btn-outline-secondary btn-sm"
        type="button"
        onClick={handleToggle}
        style={{
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          color: '#3A8DCC',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '32px',
          height: '32px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#E8F1FD';
          e.target.style.borderColor = '#3A8DCC';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#ffffff';
          e.target.style.borderColor = '#e5e7eb';
        }}
      >
        <MoreHorizontal size={16} style={{ color: '#3A8DCC' }} />
      </button>
      {isOpen &&
        (() => {
          const rect = buttonRef.current.getBoundingClientRect();
          const menuMinWidth = 180;
          const left = Math.min(
            rect.left,
            window.innerWidth - menuMinWidth - 16,
          );
          const top = rect.bottom + 8;
          const bottom = window.innerHeight - rect.top + 8;

          const menuStyle = {
            position: 'fixed',
            top: openUpward ? undefined : `${top}px`,
            bottom: openUpward ? `${bottom}px` : undefined,
            left: `${left}px`,
            zIndex: 100000,
            minWidth: `${menuMinWidth}px`,
            boxShadow:
              '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '4px 0',
            backgroundColor: 'white',
            listStyle: 'none',
            margin: 0,
            overflow: 'hidden',
          };

          return createPortal(
            <ul
              className="dropdown-menu dropdown-menu-end show"
              style={menuStyle}
            >
              <li>
                <button
                  className="dropdown-item d-flex align-items-center"
                  onClick={handleView}
                  style={{
                    padding: '10px 16px',
                    fontSize: '14px',
                    color: '#374151',
                    border: 'none',
                    backgroundColor: 'transparent',
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family)',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <Eye
                    size={16}
                    style={{
                      marginRight: '10px',
                      color: '#6b7280',
                      flexShrink: 0,
                    }}
                  />
                  View Invoice
                </button>
              </li>
              <li>
                <button
                  className="dropdown-item d-flex align-items-center"
                  onClick={handleEdit}
                  style={{
                    padding: '10px 16px',
                    fontSize: '14px',
                    color: '#374151',
                    border: 'none',
                    backgroundColor: 'transparent',
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family)',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <Edit
                    size={16}
                    style={{
                      marginRight: '10px',
                      color: '#6b7280',
                      flexShrink: 0,
                    }}
                  />
                  Edit Invoice
                </button>
              </li>
              {invoice?.status === 'Pending Review' ||
              invoice?.status === 'Requires Review' ? (
                <li>
                  <button
                    className="dropdown-item d-flex align-items-center"
                    onClick={handleProcess}
                    style={{
                      padding: '10px 16px',
                      fontSize: '14px',
                      color: '#374151',
                      border: 'none',
                      backgroundColor: 'transparent',
                      width: '100%',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-family)',
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <FileCheck
                      size={16}
                      style={{
                        marginRight: '10px',
                        color: '#6b7280',
                        flexShrink: 0,
                      }}
                    />
                    Process Validation
                  </button>
                </li>
              ) : null}
              <li
                style={{
                  borderTop: '1px solid #e5e7eb',
                  marginTop: '4px',
                  paddingTop: '4px',
                }}
              >
                <button
                  className="dropdown-item d-flex align-items-center"
                  style={{
                    padding: '10px 16px',
                    fontSize: '14px',
                    color: '#dc2626',
                    border: 'none',
                    backgroundColor: 'transparent',
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family)',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#fef2f2';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <Trash2
                    size={16}
                    style={{
                      marginRight: '10px',
                      color: '#dc2626',
                      flexShrink: 0,
                    }}
                  />
                  Delete Invoice
                </button>
              </li>
            </ul>,
            document.body,
          );
        })()}
    </div>
  );
};

// Custom Dropdown Component for Processing Queue Actions
const ProcessingQueueActionDropdown = ({
  batch,
  onView,
  onDownload,
  onDelete,
  onProcess,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 200;

      setOpenUpward(spaceBelow < menuHeight);
    }
    setIsOpen(!isOpen);
  };

  const handleView = () => {
    setIsOpen(false);
    if (batch && (batch.id || batch._id)) {
      onView(batch);
    }
  };

  const handleDownload = () => {
    setIsOpen(false);
    if (batch && (batch.id || batch._id)) {
      onDownload(batch);
    }
  };

  const handleDelete = () => {
    setIsOpen(false);
    if (batch && (batch.id || batch._id)) {
      onDelete(batch);
    }
  };

  return (
    <div
      className="dropdown"
      ref={dropdownRef}
      style={{ position: 'relative', zIndex: 1050 }}
    >
      <button
        ref={buttonRef}
        className="btn btn-outline-secondary btn-sm"
        type="button"
        onClick={handleToggle}
        style={{
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          color: '#3A8DCC',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '32px',
          height: '32px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#E8F1FD';
          e.target.style.borderColor = '#3A8DCC';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#ffffff';
          e.target.style.borderColor = '#e5e7eb';
        }}
      >
        <MoreHorizontal size={16} style={{ color: '#3A8DCC' }} />
      </button>
      {isOpen &&
        (() => {
          const rect = buttonRef.current.getBoundingClientRect();
          const menuMinWidth = 200;
          const left = Math.min(
            rect.left,
            window.innerWidth - menuMinWidth - 16,
          );
          const top = rect.bottom + 8;
          const bottom = window.innerHeight - rect.top + 8;

          const menuStyle = {
            position: 'fixed',
            top: openUpward ? undefined : `${top}px`,
            bottom: openUpward ? `${bottom}px` : undefined,
            left: `${left}px`,
            zIndex: 100000,
            minWidth: `${menuMinWidth}px`,
            backgroundColor: 'white',
            boxShadow:
              '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '4px 0',
            listStyle: 'none',
            margin: 0,
          };

          return createPortal(
            <ul className="dropdown-menu show" style={menuStyle}>
              <li>
                <button
                  className="dropdown-item d-flex align-items-center"
                  onClick={handleView}
                  style={{
                    padding: '10px 16px',
                    fontSize: '14px',
                    color: '#374151',
                    border: 'none',
                    backgroundColor: 'transparent',
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family)',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <Eye
                    size={16}
                    style={{
                      marginRight: '10px',
                      color: '#6b7280',
                      flexShrink: 0,
                    }}
                  />
                  View Batch
                </button>
              </li>
              {batch?.status === 'Completed' && (
                <li>
                  <button
                    className="dropdown-item d-flex align-items-center"
                    onClick={handleDownload}
                    style={{
                      padding: '10px 16px',
                      fontSize: '14px',
                      color: '#374151',
                      border: 'none',
                      backgroundColor: 'transparent',
                      width: '100%',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-family)',
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Download
                      size={16}
                      style={{
                        marginRight: '10px',
                        color: '#6b7280',
                        flexShrink: 0,
                      }}
                    />
                    Download Results
                  </button>
                </li>
              )}
              <li
                style={{
                  borderTop: '1px solid #e5e7eb',
                  marginTop: '4px',
                  paddingTop: '4px',
                }}
              >
                <button
                  className="dropdown-item d-flex align-items-center"
                  onClick={handleDelete}
                  style={{
                    padding: '10px 16px',
                    fontSize: '14px',
                    color: '#dc2626',
                    border: 'none',
                    backgroundColor: 'transparent',
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family)',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#fef2f2';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <Trash2
                    size={16}
                    style={{
                      marginRight: '10px',
                      color: '#dc2626',
                      flexShrink: 0,
                    }}
                  />
                  Delete Batch
                </button>
              </li>
              <li>
                <button
                  className="dropdown-item d-flex align-items-center"
                  onClick={() => {
                    setIsOpen(false);
                    onProcess(batch);
                  }} // Call handleProcess
                  style={{
                    padding: '10px 16px',
                    fontSize: '14px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family)',
                    transition: 'background-color 0.2s ease',
                    color: '#059669',
                  }}
                >
                  <CheckCircle
                    size={16}
                    style={{ marginRight: '10px', color: '#10b981' }}
                  />
                  Process Validation
                </button>
              </li>
            </ul>,
            document.body,
          );
        })()}
    </div>
  );
};

const InvoiceValidation = () => {
  const { success, error: showError } = useToast();
  const [activeTab, setActiveTab] = useState('validation');
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    invoiceNumber: '',
    invoiceDate: '',
    vendorName: '',
    amount: '',
    currency: 'USD',
    validationMethod: 'Rule-Based',
  });

  const [invoiceValidations, setInvoiceValidations] = useState([]);
  const [processingQueue, setProcessingQueue] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [settings, setSettings] = useState(null);

  const stats = {
    totalInvoices: analytics?.totalInvoices || 0,
    validatedInvoices: analytics?.validatedInvoices || 0,
    pendingReview: analytics?.pendingInvoices || 0,
    rejectedInvoices: analytics?.rejectedInvoices || 0,
    totalAmount: analytics?.totalAmount || 0,
    processingBatches: processingQueue.length,
  };

  const tabs = [
    { id: 'validation', label: 'Invoice Validation' },
    { id: 'processing', label: 'Processing Queue' },
    { id: 'analytics', label: 'Validation Analytics' },
    { id: 'settings', label: 'AI Settings' },
  ];

  const formatCurrency = (value, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
      value || 0,
    );

  const loadValidations = async () => {
    try {
      setLoading(true);
      const response = await invoiceValidationService.getValidationQueue();
      if (response.success) {
        const validations = (response.data.validations || []).map((inv) => ({
          ...inv,
          confidence:
            inv.ocrData?.confidence != null
              ? Math.round(inv.ocrData.confidence * 100)
              : null,
        }));
        setInvoiceValidations(validations);
        setProcessingQueue(response.data.validations || []);
      }
    } catch (error) {
      showError(error.message || 'Failed to load invoice validations');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await invoiceValidationService.getValidationAnalytics();
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (error) {
      showError(error.message || 'Failed to load validation analytics');
    }
  };

  const loadSettings = async () => {
    try {
      const response = await invoiceValidationService.getValidationSettings();
      if (response.success) {
        setSettings(response.data);
      }
    } catch (error) {
      showError(error.message || 'Failed to load validation settings');
    }
  };

  useEffect(() => {
    loadValidations();
    loadAnalytics();
    loadSettings();
  }, []);

  // Define columns for Invoice Validation DataTable
  const validationColumns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />,
    },
    {
      key: 'invoiceNumber',
      label: 'Invoice Details',
      width: '280px',
      render: (value, row) => (
        <div>
          <div
            style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}
          >
            {value}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.vendorName} • {row.validationMethod} •{' '}
            {row.validationResults?.matchedContract || 'No contract'}
          </div>
        </div>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      width: '120px',
      render: (amount) => (
        <span style={{ fontWeight: '600', color: '#1f2937' }}>
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '140px',
      render: (status) => {
        const statusConfig = {
          Validated: {
            color: '#10b981',
            bg: '#d1fae5',
            icon: <CheckCircle size={14} />,
          },
          'Requires Review': {
            color: '#f59e0b',
            bg: '#fef3c7',
            icon: <Clock size={14} />,
          },
          Pending: {
            color: '#3b82f6',
            bg: '#dbeafe',
            icon: <Clock size={14} />,
          },
          Rejected: {
            color: '#ef4444',
            bg: '#fee2e2',
            icon: <XCircle size={14} />,
          },
        };
        const config = statusConfig[status] || statusConfig.Pending;

        return (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: config.bg,
              color: config.color,
              width: 'fit-content',
            }}
          >
            {config.icon}
            {status}
          </span>
        );
      },
    },
    {
      key: 'confidence',
      label: 'Confidence',
      width: '100px',
      render: (value, row) => {
        const ocrData = row.ocrData;
        const confidence = ocrData?.confidence;
        const confidenceValue =
          confidence != null ? Math.round(confidence * 100) : null;

        if (confidenceValue == null) {
          return (
            <span style={{ color: '#6b7280', fontSize: '12px' }}>N/A</span>
          );
        }

        const confidenceConfig = {
          High: { color: '#10b981', bg: '#d1fae5' },
          Medium: { color: '#f59e0b', bg: '#fef3c7' },
          Low: { color: '#ef4444', bg: '#fee2e2' },
        };
        const tier =
          confidenceValue >= 85
            ? 'High'
            : confidenceValue >= 60
              ? 'Medium'
              : 'Low';
        const config = confidenceConfig[tier];

        return (
          <span
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: config.bg,
              color: config.color,
            }}
          >
            {confidenceValue}%
          </span>
        );
      },
    },
    {
      key: 'invoiceDate',
      label: 'Invoice Date',
      width: '120px',
      render: (date) => (date ? new Date(date).toLocaleDateString() : '—'),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      render: (value, row) => {
        if (!row || (!row.id && !row._id)) {
          return <span style={{ color: 'red' }}>Error</span>;
        }
        return (
          <InvoiceValidationActionDropdown
            invoice={row}
            onView={handleViewInvoice}
            onEdit={handleEditInvoice}
            onProcess={handleProcess}
            onDelete={handleDeleteInvoice}
          />
        );
      },
    },
  ];

  const processingColumns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />,
    },
    {
      key: 'invoiceNumber',
      label: 'Queued Invoice',
      width: '260px',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '500', color: '#1f2937' }}>
            {row.invoiceNumber}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.vendorName} •{' '}
            <span className="text-blue-600 font-medium">{row.status}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'priority',
      label: 'Priority',
      width: '100px',
      render: (_, row) => {
        const p = row.processingQueue?.priority;
        // Map schema numbers (1-5) to UI labels
        const priorityMap = {
          1: 'Critical',
          2: 'High',
          3: 'Medium',
          4: 'Low',
          5: 'Very Low',
        };
        return (
          <span
            className={`px-2 py-0.5 rounded text-[11px] font-bold ${p <= 2 ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-600'}`}
          >
            {priorityMap[p] || 'Standard'}
          </span>
        );
      },
    },
    {
      key: 'queuedAt',
      label: 'Queued At',
      width: '140px',
      render: (_, row) =>
        row.processingQueue?.queuedAt
          ? new Date(row.processingQueue.queuedAt).toLocaleDateString()
          : '—',
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '100px', // Increased width slightly
      // Add a specific alignment to help the dropdown find room
      align: 'right',
      render: (_, row) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            paddingRight: '8px',
          }}
        >
          <ProcessingQueueActionDropdown
            batch={row}
            onView={handleViewInvoice}
            onDelete={handleDeleteInvoice}
            // PASS THE PROCESS FUNCTION HERE
            onProcess={() => handleProcess(row)}
          />
        </div>
      ),
    },
  ];

  const handleViewInvoice = (invoice) => {
    setSelectedItem(invoice);
    success('Viewing invoice details');
    console.log('View invoice:', invoice);
  };

  const handleEditInvoice = (invoice) => {
    setSelectedItem(invoice);
    success('Opening invoice editor');
    console.log('Edit invoice:', invoice);
  };

  const handleProcess = (invoice) => {
    setSelectedItem(invoice);
    setShowProcessModal(true);
  };

  const handleDeleteInvoice = (invoice) => {
    setSelectedItem(invoice);
    // Show delete confirmation modal
    console.log('Delete invoice:', invoice);
  };

  const handleRefresh = () => {
    loadValidations();
    loadAnalytics();
  };

  const handleHelpClick = () => {
    const helpUrl = '/help/invoice-validation';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no',
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  const handleUploadInvoice = () => {
    setUploadFormData({
      invoiceNumber: '',
      invoiceDate: '',
      vendorName: '',
      amount: '',
      currency: 'USD',
      validationMethod: 'Rule-Based',
    });
    setShowUploadModal(true);
  };

  const handleUploadSubmit = async (event) => {
    event.preventDefault();
    setIsProcessing(true);
    try {
      const payload = {
        invoiceNumber: uploadFormData.invoiceNumber,
        invoiceDate: uploadFormData.invoiceDate,
        vendorName: uploadFormData.vendorName,
        amount: Number(uploadFormData.amount),
        currency: uploadFormData.currency,
        validationMethod: uploadFormData.validationMethod,
      };
      await invoiceValidationService.validateInvoice(payload);
      success('Invoice submitted for validation');
      setShowUploadModal(false);
      await loadValidations();
      await loadAnalytics();
    } catch (error) {
      showError(error.message || 'Failed to submit invoice');
    } finally {
      setIsProcessing(false);
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '',
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)',
    },
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh data',
      disabled: loading,
    },
    {
      label: 'Upload Invoice',
      icon: <Upload size={18} />,
      onClick: handleUploadInvoice,
      variant: 'primary',
      tooltip: 'Upload invoice for validation',
    },
  ];

  const handleProcessConfirm = async (status = 'Validated') => {
    setIsProcessing(true);
    try {
      if (selectedItem?._id) {
        await invoiceValidationService.updateValidationStatus(
          selectedItem._id,
          status, // This allows you to pass 'Validated' or 'Rejected'
        );
        await loadValidations();
        await loadAnalytics();
        success(`Invoice ${status === 'Validated' ? 'Approved' : 'Rejected'}`);
      }
      setShowProcessModal(false);
      setSelectedItem(null);
    } catch (error) {
      showError(error.message || 'Action failed');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Invoice Validation"
        description="AI-powered invoice validation with OCR, contract matching, and automated compliance checking"
        actions={headerActions}
      />
      <div className="page-container">
        {/* KPI Cards Grid – StatCard reference styling */}
        <div className="grid-modern-4 w-full gap-6 mb-8">
          {/* Validated Invoices */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} /> +{stats.validatedInvoices}
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <CheckCircle size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.validatedInvoices}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Validated Invoices
                </div>
              </div>
            </div>
          </div>

          {/* Pending Review */}
          {/* Pending Review – StatCard styling with bottom note moved */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />

            <div className="relative flex items-center gap-3 p-4">
              {/* Status – top right */}
              <div className="absolute right-4 top-3 text-[11px] font-semibold px-2 py-1 rounded bg-[#FEF3C7] text-[#92400E]">
                Action Required
              </div>

              {/* Icon */}
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <Clock size={20} style={{ color: '#2D7DD2' }} />
              </div>

              {/* Text */}
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.pendingReview}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Pending Review
                </div>
              </div>
            </div>
          </div>

          {/* Validation Success Rate */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} /> +2.3%
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <Bot size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.totalInvoices
                    ? Math.round(
                        (stats.validatedInvoices / stats.totalInvoices) * 100,
                      )
                    : 0}
                  %
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Validation Success Rate
                </div>
              </div>
            </div>
          </div>

          {/* Processing Batches */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} /> +{stats.activeBatches}
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <Activity size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.processingBatches}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Processing Batches
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <div className="flex bg-white border-b border-slate-200">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
          flex items-center justify-center gap-2 px-5 py-3 w-full
          text-sm font-medium
          border-b-2 transition-colors duration-200
          ${
            activeTab === tab.id
              ? '!border-b-2 border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
              : 'border-transparent text-slate-500 hover:bg-slate-100'
          }
        `}
              >
                {tab.id === 'validation' && (
                  <FileCheck
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.id === 'processing' && (
                  <Activity
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.id === 'analytics' && (
                  <BarChart3
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.id === 'settings' && (
                  <Settings
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Invoice Validation Tab */}
        {activeTab === 'validation' && (
          <div>
            {/* Validation DataTable */}
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <DataTable
                data={invoiceValidations}
                columns={validationColumns}
                title={`Invoice Validation (${invoiceValidations.length} invoices)`}
                loading={loading}
                searchable={false}
                sortable={true}
                exportable={true}
                pagination={true}
                pageSize={10}
                showActions={false}
              />
            </div>
          </div>
        )}

        {/* Processing Queue Tab */}
        {activeTab === 'processing' && (
          <div style={{ paddingBottom: '150px' }}>
            {' '}
            {/* Extra space for dropdowns */}
            <div style={{ overflowX: 'visible', maxWidth: '100%' }}>
              <DataTable
                data={processingQueue}
                columns={processingColumns}
                title={`Processing Queue (${processingQueue.length} items)`}
                loading={loading}
                searchable={false}
                sortable={true}
                exportable={true}
                pagination={true}
                pageSize={10}
                showActions={false}
              />
            </div>
          </div>
        )}

        {/* Other Tabs */}
        {activeTab === 'analytics' && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '16px',
              }}
            >
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Validated Invoices
                </div>
                <div style={{ fontWeight: 600 }}>{stats.validatedInvoices}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Rejected Invoices
                </div>
                <div style={{ fontWeight: 600 }}>{stats.rejectedInvoices}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Pending Review
                </div>
                <div style={{ fontWeight: 600 }}>{stats.pendingReview}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Total Amount
                </div>
                <div style={{ fontWeight: 600 }}>
                  {formatCurrency(stats.totalAmount)}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '16px',
              }}
            >
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Required Fields
                </div>
                <div style={{ fontWeight: 600 }}>
                  {settings?.requiredFields?.join(', ') || '—'}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Amount Tolerance
                </div>
                <div style={{ fontWeight: 600 }}>
                  {settings?.amountTolerancePercent || 0}%
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Auto Reject on Error
                </div>
                <div style={{ fontWeight: 600 }}>
                  {settings?.autoRejectOnError ? 'Enabled' : 'Disabled'}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Auto Match Contracts
                </div>
                <div style={{ fontWeight: 600 }}>
                  {settings?.autoMatchContracts ? 'Enabled' : 'Disabled'}
                </div>
              </div>
            </div>
          </div>
        )}

        {showUploadModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1050,
              padding: '20px',
            }}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                setShowUploadModal(false);
              }
            }}
          >
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                width: '100%',
                maxWidth: '520px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              }}
            >
              <h3 style={{ marginTop: 0, marginBottom: '16px' }}>
                Submit Invoice
              </h3>
              <form onSubmit={handleUploadSubmit}>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Invoice Number
                    <input
                      type="text"
                      value={uploadFormData.invoiceNumber}
                      onChange={(event) =>
                        setUploadFormData({
                          ...uploadFormData,
                          invoiceNumber: event.target.value,
                        })
                      }
                      required
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Invoice Date
                    <input
                      type="date"
                      value={uploadFormData.invoiceDate}
                      onChange={(event) =>
                        setUploadFormData({
                          ...uploadFormData,
                          invoiceDate: event.target.value,
                        })
                      }
                      required
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Vendor Name
                    <input
                      type="text"
                      value={uploadFormData.vendorName}
                      onChange={(event) =>
                        setUploadFormData({
                          ...uploadFormData,
                          vendorName: event.target.value,
                        })
                      }
                      required
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Amount
                    <input
                      type="number"
                      step="0.01"
                      value={uploadFormData.amount}
                      onChange={(event) =>
                        setUploadFormData({
                          ...uploadFormData,
                          amount: event.target.value,
                        })
                      }
                      required
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Currency
                    <input
                      type="text"
                      value={uploadFormData.currency}
                      onChange={(event) =>
                        setUploadFormData({
                          ...uploadFormData,
                          currency: event.target.value,
                        })
                      }
                      required
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Validation Method
                    <select
                      value={uploadFormData.validationMethod}
                      onChange={(event) =>
                        setUploadFormData({
                          ...uploadFormData,
                          validationMethod: event.target.value,
                        })
                      }
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <option value="Rule-Based">Rule-Based</option>
                      <option value="Manual">Manual</option>
                      <option value="AI-OCR">AI-OCR</option>
                    </select>
                  </label>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '10px',
                    marginTop: '18px',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#ffffff',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: 'var(--primary-color)',
                      color: '#ffffff',
                    }}
                    disabled={isProcessing}
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Process Validation Modal */}
        <ConfirmationModal
          isOpen={showProcessModal}
          onClose={() => setShowProcessModal(false)}
          onConfirm={handleProcessConfirm}
          title="Process Validation"
          message={`Process validation for invoice "${selectedItem?.invoiceNumber}"? This will update validation status.`}
          confirmText="Process"
          cancelText="Cancel"
          type="success"
          isLoading={isProcessing}
        />
      </div>
    </>
  );
};

export default InvoiceValidation;
